import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { LogOut, Download } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function BusinessDashboard({ auth, onLogout }) {
  const [qrCode, setQrCode] = useState("");
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch QR Code
      const qrResponse = await axios.get(`${BACKEND_URL}/api/businesses/${auth.business_id}/qrcode`);
      setQrCode(qrResponse.data.qr_code);

      // Fetch Reviews
      const reviewsResponse = await axios.get(`${BACKEND_URL}/api/reviews/${auth.business_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviews(reviewsResponse.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const downloadQRCode = () => {
    const link = document.createElement('a');
    link.href = qrCode;
    link.download = `${auth.business_name}-qr-code.png`;
    link.click();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="business-dashboard">
      <div className="border-b bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold">{auth.business_name}</h1>
            <p className="text-sm text-muted-foreground">Business Dashboard</p>
          </div>
          <Button onClick={onLogout} variant="outline" data-testid="logout-button">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* QR Code Card */}
        <Card>
          <CardHeader>
            <CardTitle>Your QR Code</CardTitle>
            <CardDescription>Display this QR code for customers to scan and leave reviews</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center space-y-4">
            {qrCode && <img src={qrCode} alt="QR Code" className="w-64 h-64 border rounded" data-testid="business-qr-code" />}
            <Button onClick={downloadQRCode} data-testid="download-qr-button">
              <Download className="w-4 h-4 mr-2" />
              Download QR Code
            </Button>
          </CardContent>
        </Card>

        {/* Reviews Card */}
        <Card>
          <CardHeader>
            <CardTitle>Customer Reviews</CardTitle>
            <CardDescription>All customer feedback and ratings</CardDescription>
          </CardHeader>
          <CardContent>
            {reviews.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No reviews yet</p>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="text-sm text-green-600 font-medium">High Ratings (4-5★)</div>
                    <div className="text-2xl font-bold text-green-700">
                      {reviews.filter(r => r.rating >= 4).length}
                    </div>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="text-sm text-amber-600 font-medium">Medium Ratings (3★)</div>
                    <div className="text-2xl font-bold text-amber-700">
                      {reviews.filter(r => r.rating === 3).length}
                    </div>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="text-sm text-red-600 font-medium">Low Ratings (1-2★)</div>
                    <div className="text-2xl font-bold text-red-700">
                      {reviews.filter(r => r.rating <= 2).length}
                    </div>
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer Name</TableHead>
                      <TableHead>Mobile</TableHead>
                      <TableHead>Rating</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reviews.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((review) => (
                      <TableRow key={review.id}>
                        <TableCell className="font-medium">{review.customer_name}</TableCell>
                        <TableCell>{review.customer_mobile}</TableCell>
                        <TableCell>
                          <span className={`font-semibold ${
                            review.rating >= 4 ? 'text-green-600' : 
                            review.rating === 3 ? 'text-amber-600' : 
                            'text-red-600'
                          }`}>
                            {review.rating} ★
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            review.rating >= 4 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {review.rating >= 4 ? 'Sent to Google' : 'Internal Only'}
                          </span>
                        </TableCell>
                        <TableCell>{new Date(review.created_at).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}